<template>
    <div ref="chartContainer">
        <b-container class="px-0">
            <card-layout
                title=""
                class="mt-1"
                :display-card="false"

            >
              <apexchart :height="chartHeight" type="donut" :options="chartOptions" :series="series"></apexchart>
            </card-layout>
        </b-container>
    </div>
</template>

<script setup>
    import { onMounted, onUnmounted, computed, ref, watch} from 'vue';
    import { storeToRefs } from 'pinia';
    import CardLayout from '../details/CardLayout.vue'
    import styles from '../../../sass/_export.module.scss';


    // Stores
    import { useToplinesStore } from '../../stores/toplines';

    const toplinesStore = useToplinesStore();

    // Store refs
    const {
        toplineData,
    } = storeToRefs(toplinesStore);

    // Props
    const props = defineProps({
        ie_user : {required: true, type: Boolean}
    });

    const series = computed(() => {
      if (toplineData.value !== null && Object.hasOwn(toplineData.value, 'topline')) {
        // Get a count of the categories
        const toplineCounts = Object.values(toplineData.value['topline']).reduce((acc, t) => {
          acc[t.category] = (acc[t.category] || 0) + 1;
          return acc;
        }, {});
        // Map to get an array of the counts in the same order as the labels
        const toplineCountsArray = chartOptions.value.labels.map(label => toplineCounts[label] || 0);
        return toplineCountsArray;
      }
      return [];
    });

    const pollThirtyDayTotals = computed(() => {
      if (toplineData.value !== null && Object.hasOwn(toplineData.value, 'topline')) {
        let today = new Date()
        const pollThirtyDayCounts = Object.values(toplineData.value['topline']).reduce((acc, t) => {
          let count = 0;
          for(let p in t["poll"]) {
            if(t["poll"][p].hasOwnProperty("date")) {
              let tmpdate = new Date(t["poll"][p]["date"]);
              let days = (today.getTime()-tmpdate.getTime()) / (1000 * 3600 * 24);

              count = days <= 30 ? 1 : 0;
            }
            acc[t.category] = (acc[t.category] || 0) + count;
          }
          return acc;
        }, {});

        const pollThirtyDayTotalsArray = chartOptions.value.labels.map(label => pollThirtyDayCounts[label] || 0);
        return pollThirtyDayTotalsArray;
      }
    });

    const pollTotals = computed(() => {
      if (toplineData.value !== null && Object.hasOwn(toplineData.value, 'topline')) {
        // Get a count of the categories
        const pollCounts = Object.values(toplineData.value['topline']).reduce((acc, t) => {
          // let count = t.poll.total > 0 ? 1 : 0;
          let count = (t.poll.total && t.poll.total > 0) ? t.poll.total : 0;
          acc[t.category] = (acc[t.category] || 0) + count;
          return acc;
        }, {});
        // console.log(pollCounts);

        // Map to get an array of the counts in the same order as the labels
        const pollCountsArray = chartOptions.value.labels.map(label => pollCounts[label] || 0);
        return pollCountsArray;
      }
      return [];
    });
   
    // Track component width
    const componentWidth = ref(0);
    const chartContainer = ref(null);

    // ResizeObserver to watch component size
    let resizeObserver = null;

    const chartHeight = computed(() => {
      if (componentWidth.value >= 1000) return 560;
      if (componentWidth.value >= 950) return 520;
      if (componentWidth.value >= 900) return 480;
      if (componentWidth.value >= 850) return 440;
      // if (componentWidth.value >= 800) return 420;
      if (componentWidth.value >= 750) return 420;
      // if (componentWidth.value >= 700) return 380;
      if (componentWidth.value >= 650) return 380;
      // if (componentWidth.value >= 600) return 320;
      if (componentWidth.value >= 550) return 320;
      // if (componentWidth.value >= 500) return 280;
      if (componentWidth.value >= 400) return 280;
      return 280;
    });

    // Computed property for dynamic grid padding
    const gridBottomPadding = computed(() => {
      if (componentWidth.value >= 800) return -380;
      if (componentWidth.value >= 750) return -350;
      if (componentWidth.value >= 700) return -300;
      // if (componentWidth.value >= 650) return -200;
      // if (componentWidth.value >= 600) return -200;
      // if (componentWidth.value >= 550) return -200;
      // if (componentWidth.value >= 500) return -200;
      if (componentWidth.value >= 450) return -200;
      return -150;
    });

    // Watch for changes to gridBottomPadding and update chartOptions
    watch(gridBottomPadding, (newPadding) => {
      chartOptions.value = {
        ...chartOptions.value,
        grid: {
          padding: {
            bottom: newPadding
          }
        }
      };
    });

    onMounted(() => {
      console.log('TotalsProjectionsRadial Component mounted.');

      // Set initial width
      if (chartContainer.value) {
        componentWidth.value = chartContainer.value.offsetWidth;

        // Create ResizeObserver to watch component size
        resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            componentWidth.value = entry.contentRect.width;
          }
        });

        resizeObserver.observe(chartContainer.value);
      }
    });

    onUnmounted(() => {
      if (resizeObserver && chartContainer.value) {
        resizeObserver.unobserve(chartContainer.value);
        resizeObserver.disconnect();
      }
    });

    const chartColors = ['#002D72','#1D4F91','#0077C8','#00BCE7','#c0c0c0', '#ff7b67','#e6342f','#9b0000','#580000'];

    const chartLabels =
            [
              'Super Safe Dem',
              'Safe Dem',
              'Likely Dem',
              'Lean Dem',
              'Toss Up',
              'Lean Rep',
              'Likely Rep',
              'Safe Rep',
              'Super Safe Rep',
            ];

    const chartOptions = ref({
            chart: {
              type: 'donut',
              background: styles.white,
              animations: {
                enabled: false
              },
            },
            responsive: [{
              breakpoint: 480,
              options: {
                chart: {
                  width: 200
                },
                legend: {
                  position: 'bottom'
                }
              }
            }],
            plotOptions: {
              pie: {
                startAngle: -90,
                endAngle: 90,
                offsetY: 10,
                dataLabels: {
                  offset: 0,
                  minAngleToShowLabel: 5
                },
                donut: {
                  size: '30%',
                }
              },
            },
            dataLabels: {
              enabled: true,
              formatter: function(val, opts) {
                // opts.w.config.series[opts.seriesIndex] gives you the actual count
                return opts.w.config.series[opts.seriesIndex];
              },
              style: {
                fontSize: '14px',
              },
            },
            states: {
              hover: {
                filter: {
                  type: 'darken'
                },
              }
            },
            colors: chartColors,
            labels: chartLabels,
            grid: {
              padding: {
                bottom: gridBottomPadding.value
              }
            },
            legend: {
              position: 'bottom',
            },
            tooltip: {
              shared: true,
              intersect: false,
              // custom: props.ie_user ? undefined : customTooltipFormatter,
              style: {
                fontSize: '14px',
              }
            },
          });


    onMounted(() => {
        console.log('TotalsHistoricalTimeline Component mounted.');
    });

  //Use apexcharts css classes pulled from the tooltip code to match default apexcharts styling
  //https://github.com/apexcharts/apexcharts.js/blob/main/src/modules/tooltip/Tooltip.js
  function customTooltipFormatter( {series, seriesIndex, dataPointIndex, w}) {
    return `
      <div style="background: ${styles.white}; color: #333;">
        <div class="apexcharts-tooltip-title" style="font-family: Lato, sans-serif; font-size: 14px; background-color: ${chartColors[seriesIndex]}; color: ${styles.white};">${chartLabels[seriesIndex]}: ${series[seriesIndex]}</div>
        <div class="apexcharts-tooltip-text mx-2" style="font-family: Lato, sans-serif; font-size: 14px; background: white; color: #333;">
          <!--<div class="apexcharts-tooltip-y-group d-flex">
              <span class="apexcharts-tooltip-text-y-label me-1">Last 30 Days: </span>
              <span class="apexcharts-tooltip-text-y-value ms-auto">${pollThirtyDayTotals.value[seriesIndex]}</span>
          </div>-->
          <div class="apexcharts-tooltip-y-group d-flex">
              <span class="apexcharts-tooltip-text-y-label me-1">Total Polls: </span>
              <span class="apexcharts-tooltip-text-y-value ms-auto">${pollTotals.value[seriesIndex]}</span>
          </div>
        </div>
      </div>
      `;
  }
</script>
<style>
</style>